import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { Modal, ScrollTable, PageHeader, EmptyState, PAGE_SIZE } from '../../components/UI'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// ── Avatar helpers ───────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  '#3b82f6','#8b5cf6','#f59e0b','#ef4444',
  '#10b981','#ec4899','#06b6d4','#f97316',
]
const avatarColor = name =>
  AVATAR_COLORS[name
    ? Array.from(name).reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length
    : 0]
const initials = name => (name ? name.slice(0, 2) : '?')

// ── Shipment status badge ────────────────────────────────────────────────────
const shipStatus = s => ({
  pending:   { label: '待處理', color: '#f59e0b', bg: '#fffbeb' },
  confirmed: { label: '已確認', color: '#3b82f6', bg: '#eff6ff' },
  shipped:   { label: '已出貨', color: '#10b981', bg: '#f0fdf4' },
  cancelled: { label: '已取消', color: '#94a3b8', bg: '#f1f5f9' },
}[s] || { label: s || '-', color: '#94a3b8', bg: '#f1f5f9' })

// ── CSV export ───────────────────────────────────────────────────────────────
function exportCSV(customers) {
  const rows = [
    ['客戶名稱','統一編號','聯絡人','電話','Email','地址','備注','建立日期'],
    ...customers.map(c => [
      c.name, c.tax_id, c.contact, c.phone,
      c.email, c.address, c.note, formatDate(c.created_at),
    ]),
  ]
  const csv = rows.map(r =>
    r.map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(',')
  ).join('\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `客戶列表_${new Date().toLocaleDateString('zh-TW').replace(/\//g,'-')}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ── Customer Avatar component ────────────────────────────────────────────────
function Avatar({ name, size = 40 }) {
  const color = avatarColor(name)
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color + '22', border: `2px solid ${color}44`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 800, color, flexShrink: 0,
      letterSpacing: '-1px',
    }}>
      {initials(name)}
    </div>
  )
}

// ── Customer Card (card grid view) ───────────────────────────────────────────
function CustomerCard({ c, isAdmin, onEdit, onDelete, onProfile }) {
  const color = avatarColor(c.name)
  return (
    <div
      onClick={() => onProfile(c)}
      style={{
        background: '#fff', borderRadius: 16,
        border: `1.5px solid ${C.border}`,
        padding: '18px 20px', cursor: 'pointer',
        transition: 'box-shadow 0.18s, border-color 0.18s',
        display: 'flex', flexDirection: 'column', gap: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.09)'; e.currentTarget.style.borderColor = color + '66' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = C.border }}
    >
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <Avatar name={c.name} size={46} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {c.name}
          </div>
          {c.tax_id && (
            <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace' }}>統編：{c.tax_id}</div>
          )}
          {c.contact && (
            <div style={{ fontSize: 12, color: C.sub, marginTop: 1 }}>👤 {c.contact}</div>
          )}
        </div>
      </div>

      {/* Contact info */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
        {c.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text }}>
            <span style={{ fontSize: 14 }}>📞</span>
            <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()} style={{ color: C.accent, textDecoration: 'none', fontWeight: 600 }}>
              {c.phone}
            </a>
          </div>
        )}
        {c.email && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.muted, overflow: 'hidden' }}>
            <span style={{ fontSize: 13 }}>✉️</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</span>
          </div>
        )}
        {c.address && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: C.muted }}>
            <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>📍</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.address}</span>
          </div>
        )}
        {!c.phone && !c.email && !c.address && (
          <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>尚無聯絡資訊</div>
        )}
      </div>

      {/* Note */}
      {c.note && (
        <div style={{ fontSize: 11, color: C.muted, background: '#f8fafc', borderRadius: 8, padding: '6px 10px', marginBottom: 12,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📝 {c.note}
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 10, color: C.muted }}>建立 {formatDate(c.created_at)}</span>
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          <button style={{ ...st.btn('warning'), padding: '5px 10px', fontSize: 11 }} onClick={() => onEdit(c)}>編輯</button>
          {isAdmin && (
            <button style={{ ...st.btn('danger'), padding: '5px 10px', fontSize: 11 }} onClick={() => onDelete(c.id, c.name)}>刪除</button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Customer Profile Modal ────────────────────────────────────────────────────
function ProfileModal({ customer, onClose, onEdit }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)
  const [shipments, setShipments] = useState([])
  const [loadingShip, setLoadingShip] = useState(true)
  const color = avatarColor(customer.name)

  useEffect(() => {
    setLoadingShip(true)
    supabase.from('shipments')
      .select('id, shipment_no, status, created_at, updated_at')
      .eq('customer_name', customer.name)
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) => {
        setShipments(data || [])
        setLoadingShip(false)
      })
  }, [customer.name])

  const infoRows = [
    { icon: '🏢', label: '公司名稱', value: customer.name },
    { icon: '🔢', label: '統一編號', value: customer.tax_id },
    { icon: '👤', label: '聯絡人',   value: customer.contact },
    { icon: '📞', label: '電話',     value: customer.phone, link: customer.phone ? `tel:${customer.phone}` : null },
    { icon: '✉️', label: 'Email',    value: customer.email, link: customer.email ? `mailto:${customer.email}` : null },
    { icon: '📍', label: '地址',     value: customer.address },
  ].filter(r => r.value)

  return (
    <Modal title="" onClose={onClose} wide>
      {/* Profile header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '4px 0 20px', borderBottom: `2px solid ${C.border}`, marginBottom: 20 }}>
        <Avatar name={customer.name} size={64} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.text, marginBottom: 4 }}>{customer.name}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {customer.tax_id && (
              <span style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', background: '#f1f5f9', padding: '2px 8px', borderRadius: 8 }}>統編 {customer.tax_id}</span>
            )}
            <span style={{ fontSize: 11, color: C.muted }}>建立 {formatDate(customer.created_at)}</span>
          </div>
        </div>
        <button style={{ ...st.btn('warning'), padding: '8px 16px', fontSize: 13, flexShrink: 0 }} onClick={() => onEdit(customer)}>
          ✏️ 編輯資料
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left: contact info */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>聯絡資訊</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {infoRows.map(row => (
              <div key={row.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 15, flexShrink: 0, width: 22, textAlign: 'center' }}>{row.icon}</span>
                <div>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, marginBottom: 1 }}>{row.label}</div>
                  {row.link
                    ? <a href={row.link} style={{ fontSize: 13, color: C.accent, fontWeight: 600, textDecoration: 'none' }}>{row.value}</a>
                    : <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{row.value}</div>}
                </div>
              </div>
            ))}
            {infoRows.length === 0 && (
              <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>尚無聯絡資訊</div>
            )}
          </div>

          {/* Note */}
          {customer.note && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>備注</div>
              <div style={{ fontSize: 13, color: C.text, background: '#f8fafc', borderRadius: 10, padding: '12px 14px', lineHeight: 1.6, border: `1px solid ${C.border}` }}>
                {customer.note}
              </div>
            </div>
          )}
        </div>

        {/* Right: shipment history */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>
            {_('crmShipHistory')}
            <span style={{ fontSize: 11, color: C.muted, fontWeight: 400, marginLeft: 8 }}>近 20 筆</span>
          </div>
          {loadingShip ? (
            <div style={{ fontSize: 12, color: C.muted, padding: 16, textAlign: 'center' }}>{_('loading')}</div>
          ) : shipments.length === 0 ? (
            <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', padding: '20px 0', background: '#f8fafc', borderRadius: 10 }}>
              {_('crmNoShipHistory')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
              {shipments.map(s => {
                const st2 = shipStatus(s.status)
                return (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 10, border: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, fontFamily: 'monospace' }}>{s.shipment_no}</div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{formatDate(s.created_at)}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: st2.color, background: st2.bg, padding: '3px 10px', borderRadius: 20 }}>
                      {st2.label}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {/* Summary stats from shipments */}
          {shipments.length > 0 && (
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <div style={{ flex: 1, background: C.accentDim, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: C.accent }}>{shipments.length}</div>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>出貨單總數</div>
              </div>
              <div style={{ flex: 1, background: '#f0fdf4', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#10b981' }}>
                  {shipments.filter(s => s.status === 'shipped').length}
                </div>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>已出貨</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

// ── Main Module ───────────────────────────────────────────────────────────────
export default function CRM({ userProfile, showToast, addLog, isMobile, enabledModules = [] }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)
  const isAdmin = userProfile?.role === 'admin'
  const canDelete = isAdmin || enabledModules.includes('delete_records')

  const [customers, setCustomers] = useState([])
  const [loading,   setLoading]   = useState(true)
  const [saving,    setSaving]    = useState(false)
  const [view,      setView]      = useState(isMobile ? 'card' : 'table') // 'table' | 'card'

  const [showAdd,  setShowAdd]  = useState(false)
  const [editing,  setEditing]  = useState(null)
  const [profile,  setProfile]  = useState(null)

  const [search,   setSearch]   = useState('')
  const [page,     setPage]     = useState(1)
  const [selected, setSelected] = useState([])

  const emptyForm = () => ({ name: '', contact: '', phone: '', email: '', address: '', note: '', tax_id: '' })
  const [form, setForm] = useState(emptyForm())

  const load = async () => {
    setLoading(true)
    const cid = userProfile?.company_id
    let q = supabase.from('customers').select('*')
    if (cid) q = q.eq('company_id', cid)
    const { data } = await q.order('created_at', { ascending: false }).limit(500)
    if (data) setCustomers(data)
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  // ── Computed stats ──
  const stats = useMemo(() => ({
    total:    customers.length,
    hasPhone: customers.filter(c => c.phone).length,
    hasEmail: customers.filter(c => c.email).length,
    thisMonth: customers.filter(c => {
      const d = new Date(c.created_at)
      const now = new Date()
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
    }).length,
  }), [customers])

  // ── Filter ──
  const filtered = useMemo(() => {
    if (!search) return customers
    const q = search.toLowerCase()
    return customers.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.contact?.toLowerCase().includes(q) ||
      c.phone?.includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.address?.toLowerCase().includes(q) ||
      c.tax_id?.includes(q)
    )
  }, [customers, search])

  const paged      = useMemo(() => filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE), [filtered, page])
  const totalPages = useMemo(() => Math.ceil(filtered.length / PAGE_SIZE), [filtered])

  // ── CRUD ──
  const handleAdd = async () => {
    if (!form.name.trim()) { showToast('請填寫客戶名稱', 'error'); return }
    setSaving(true)
    const { error } = await supabase.from('customers').insert({ ...form, name: form.name.trim(), company_id: userProfile?.company_id || null })
    setSaving(false)
    if (error) { showToast('新增失敗：' + error.message, 'error'); return }
    await addLog('新增客戶', `新增客戶：${form.name}`)
    setForm(emptyForm())
    setShowAdd(false)
    showToast('✓ 客戶已新增')
    load()
  }

  const handleEdit = async () => {
    if (!editing?.name?.trim()) { showToast('客戶名稱不得為空', 'error'); return }
    setSaving(true)
    const { error } = await supabase.from('customers').update({
      name: editing.name, contact: editing.contact, phone: editing.phone,
      email: editing.email, address: editing.address, note: editing.note, tax_id: editing.tax_id,
    }).eq('id', editing.id)
    setSaving(false)
    if (error) { showToast('更新失敗：' + error.message, 'error'); return }
    await addLog('編輯客戶', `編輯客戶：${editing.name}`)
    setEditing(null)
    showToast('✓ 已更新')
    load()
  }

  const handleDelete = async (id, name) => {
    if (!window.confirm(`確定刪除客戶「${name}」？\n⚠️ 注意：歷史出貨紀錄不受影響。`)) return
    const { error } = await supabase.from('customers').delete().eq('id', id)
    if (error) { showToast('刪除失敗：' + error.message, 'error'); return }
    await addLog('刪除客戶', `刪除客戶：${name}`)
    showToast('✓ 已刪除')
    setSelected(prev => prev.filter(s => s !== id))
    load()
  }

  const handleBatchDelete = async () => {
    if (!window.confirm(`確定刪除選取的 ${selected.length} 筆客戶資料？此操作無法復原。`)) return
    const { error } = await supabase.from('customers').delete().in('id', selected)
    if (error) { showToast('批量刪除失敗', 'error'); return }
    await addLog('批量刪除客戶', `刪除 ${selected.length} 筆客戶資料`)
    showToast(`✓ 已刪除 ${selected.length} 筆`)
    setSelected([])
    load()
  }

  // Open edit from profile
  const openEdit = customer => {
    setProfile(null)
    setEditing({ ...customer })
  }

  // ── Form fields config ──
  const formFields = [
    { label: _('crmCustName'), key: 'name',    placeholder: '公司或個人名稱',   full: false },
    { label: _('crmTaxId'),   key: 'tax_id',  placeholder: '8碼統編',         full: false },
    { label: _('crmContact'), key: 'contact', placeholder: '負責人/窗口',      full: false },
    { label: '電話',           key: 'phone',   placeholder: '02-XXXX / 09XX',  full: false },
    { label: 'Email',          key: 'email',   placeholder: 'xxx@company.com', full: false },
    { label: _('crmAddress'), key: 'address', placeholder: '縣市 + 詳細地址',  full: true  },
    { label: _('crmNote'),    key: 'note',    placeholder: '補充說明、特殊需求...', full: true },
  ]

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader title="客戶管理" en="Customer Management" vi="Khách hàng" />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 22 }}>
        {[
          { icon: '👥', label: _('crmTotalCustomers'), value: stats.total,     color: C.accent,  bg: C.accentDim },
          { icon: '📞', label: _('crmHasPhone'),       value: stats.hasPhone,  color: '#10b981', bg: '#f0fdf4' },
          { icon: '✉️', label: _('crmHasEmail'),       value: stats.hasEmail,  color: '#3b82f6', bg: '#eff6ff' },
          { icon: '🆕', label: _('crmNewThisMonth'),   value: stats.thisMonth, color: '#8b5cf6', bg: '#f5f3ff' },
        ].map((s, i) => (
          <div key={i} style={{ background: s.bg, border: `2px solid ${s.color}33`, borderRadius: 14, padding: '12px 16px' }}>
            <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: C.sub, fontWeight: 600, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search */}
        <input
          placeholder={_('crmSearchPh')}
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); setSelected([]) }}
          style={{ ...st.input, width: 300, maxWidth: '100%' }}
        />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* View toggle — desktop only */}
          {!isMobile && (
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 3, gap: 2 }}>
              {[['table', _('crmViewTable')], ['card', _('crmViewCard')]].map(([v, l]) => (
                <button key={v} onClick={() => setView(v)} style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8,
                  border: 'none', cursor: 'pointer',
                  background: view === v ? '#fff' : 'transparent',
                  color: view === v ? C.accent : C.muted,
                  boxShadow: view === v ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s',
                }}>
                  {l}
                </button>
              ))}
            </div>
          )}

          {/* Export */}
          <button style={{ ...st.btn('default'), padding: '9px 14px', fontSize: 12 }}
            onClick={() => exportCSV(customers)} title="匯出 CSV">
            📥 匯出
          </button>

          {/* Batch delete */}
          {canDelete && selected.length > 0 && (
            <button style={{ ...st.btn('danger'), padding: '9px 14px' }} onClick={handleBatchDelete}>
              🗑 刪除({selected.length})
            </button>
          )}

          {/* Add customer */}
          <button style={{ ...st.btn(), padding: '9px 16px' }}
            onClick={() => { setShowAdd(!showAdd); setForm(emptyForm()) }}>
            <span>{showAdd ? '✕ ' + _('cancel') : _('crmNewCustomer')}</span>
            <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>{showAdd ? 'Cancel' : 'Add Customer'}</span>
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div style={{ ...st.card, marginBottom: 18, border: `2px solid ${C.accent}` }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 16 }}>{_('crmFormNewTitle')} · New Customer</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {formFields.filter(f => !f.full).map(f => (
              <div key={f.key}>
                <label style={st.label}>{f.label}</label>
                <input style={st.input} value={form[f.key]} placeholder={f.placeholder}
                  onChange={e => setForm(x => ({ ...x, [f.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginTop: 12 }}>
            {formFields.filter(f => f.full).map(f => (
              <div key={f.key}>
                <label style={st.label}>{f.label}</label>
                <input style={st.input} value={form[f.key]} placeholder={f.placeholder}
                  onChange={e => setForm(x => ({ ...x, [f.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '12px' }} onClick={handleAdd} disabled={saving}>
              {saving ? '儲存中...' : _('crmConfirmAdd')}
            </button>
            <button style={{ ...st.btn('default'), padding: '12px 18px' }} onClick={() => setShowAdd(false)}>{_('cancel')}</button>
          </div>
        </div>
      )}

      {/* Results count */}
      {search && (
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          搜尋「{search}」共找到 <strong style={{ color: C.text }}>{filtered.length}</strong> 筆客戶
        </div>
      )}

      {/* ── Card view ── */}
      {view === 'card' && (
        loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: C.muted }}>{_('loading')}</div>
        ) : paged.length === 0 ? (
          <EmptyState icon="👥" text={search ? _('crmNoMatch') : _('crmNoCustomers')} />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
              {paged.map(c => (
                <CustomerCard
                  key={c.id} c={c} isAdmin={canDelete}
                  onEdit={setEditing} onDelete={handleDelete}
                  onProfile={setProfile}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
                <button disabled={page === 1} style={{ ...st.btn('default'), padding: '8px 14px' }} onClick={() => setPage(p => p-1)}>{_('prevPage')}</button>
                <span style={{ padding: '8px 14px', fontSize: 13, color: C.muted }}>{page} / {totalPages}</span>
                <button disabled={page === totalPages} style={{ ...st.btn('default'), padding: '8px 14px' }} onClick={() => setPage(p => p+1)}>{_('nextPage')}</button>
              </div>
            )}
          </>
        )
      )}

      {/* ── Table view ── */}
      {view === 'table' && (
        <div style={st.card}>
          {canDelete && filtered.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>
              <input type="checkbox"
                checked={selected.length === paged.length && paged.length > 0}
                onChange={e => setSelected(e.target.checked ? paged.map(c => c.id) : [])}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span>全選本頁 ({paged.length} 筆)</span>
              <span style={{ marginLeft: 'auto' }}>{filtered.length} 位客戶</span>
            </div>
          )}
          <ScrollTable>
            <thead>
              <tr>
                {canDelete && <th style={st.th}></th>}
                {[[_('crmThName'),'Customer'],[_('crmThTaxId'),'Tax ID'],[_('crmThContact'),'Contact'],['電話','Phone'],['Email','Email'],['地址','Address'],[_('crmThCreated'),'Since'],[_('crmThAction'),'Action']].map(([zh, en]) => (
                  <th key={zh} style={st.th}>
                    <div>{zh}</div>
                    <div style={{ fontSize: 9, opacity: 0.45 }}>{en}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canDelete ? 9 : 8} style={{ textAlign: 'center', padding: 40, color: C.muted }}>{_('loading')}</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={canDelete ? 9 : 8}><EmptyState icon="👥" text={search ? _('crmNoMatch') : _('crmNoCustomers')} /></td></tr>
              ) : paged.map(c => (
                <tr key={c.id} style={{ background: selected.includes(c.id) ? C.accentDim : 'transparent', cursor: 'pointer' }}
                  onClick={() => setProfile(c)}>
                  {canDelete && (
                    <td style={st.td} onClick={e => e.stopPropagation()}>
                      <input type="checkbox"
                        checked={selected.includes(c.id)}
                        onChange={e => setSelected(prev => e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                      />
                    </td>
                  )}
                  {/* Name + avatar */}
                  <td style={{ ...st.td, fontWeight: 700 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={c.name} size={32} />
                      <div>
                        <div style={{ color: C.text, fontSize: 13 }}>{c.name}</div>
                        {c.note && <div style={{ fontSize: 10, color: C.muted, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📝 {c.note}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ ...st.td, fontFamily: 'monospace', fontSize: 12, color: C.muted }}>{c.tax_id || '-'}</td>
                  <td style={st.td}>{c.contact || '-'}</td>
                  <td style={st.td}>
                    {c.phone
                      ? <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()} style={{ color: C.accent, textDecoration: 'none', fontWeight: 600 }}>{c.phone}</a>
                      : <span style={{ color: C.muted }}>-</span>}
                  </td>
                  <td style={{ ...st.td, color: C.muted, fontSize: 12 }}>{c.email || '-'}</td>
                  <td style={{ ...st.td, color: C.muted, fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.address || '-'}</td>
                  <td style={{ ...st.td, fontSize: 11, color: C.muted }}>{formatDate(c.created_at)}</td>
                  <td style={st.td} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button style={{ ...st.btn('warning'), padding: '5px 10px', fontSize: 11 }} onClick={() => setEditing({ ...c })}>
                        編輯
                      </button>
                      {canDelete && (
                        <button style={{ ...st.btn('danger'), padding: '5px 10px', fontSize: 11 }} onClick={() => handleDelete(c.id, c.name)}>
                          刪除
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '12px 0' }}>
              <button disabled={page === 1} style={{ ...st.btn('default'), padding: '8px 14px' }} onClick={() => setPage(p => p-1)}>{_('prevPage')}</button>
              <span style={{ padding: '8px 14px', fontSize: 13, color: C.muted }}>{page} / {totalPages}</span>
              <button disabled={page === totalPages} style={{ ...st.btn('default'), padding: '8px 14px' }} onClick={() => setPage(p => p+1)}>{_('nextPage')}</button>
            </div>
          )}
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editing && (
        <Modal title={`${_('crmEditTitle')} — ${editing.name}`} onClose={() => setEditing(null)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, padding: '12px 16px', background: '#f8fafc', borderRadius: 12 }}>
            <Avatar name={editing.name} size={48} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{editing.name}</div>
              <div style={{ fontSize: 11, color: C.muted }}>建立：{formatDate(editing.created_at)}</div>
            </div>
          </div>

          {/* Non-full fields */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
            {formFields.filter(f => !f.full).map(f => (
              <div key={f.key}>
                <label style={st.label}>{f.label}</label>
                <input style={st.input} value={editing[f.key] || ''} placeholder={f.placeholder}
                  onChange={e => setEditing(x => ({ ...x, [f.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          {/* Full-width fields */}
          {formFields.filter(f => f.full).map(f => (
            <div key={f.key} style={{ marginBottom: 12 }}>
              <label style={st.label}>{f.label}</label>
              <input style={st.input} value={editing[f.key] || ''} placeholder={f.placeholder}
                onChange={e => setEditing(x => ({ ...x, [f.key]: e.target.value }))} />
            </div>
          ))}

          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '12px' }} onClick={handleEdit} disabled={saving}>
              {saving ? '儲存中...' : _('crmConfirmUpdate')}
            </button>
            <button style={{ ...st.btn('default'), padding: '12px 18px' }} onClick={() => setEditing(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── Profile Modal ── */}
      {profile && (
        <ProfileModal
          customer={profile}
          onClose={() => setProfile(null)}
          onEdit={openEdit}
        />
      )}
    </div>
  )
}
